import FirebaseAnalytics from '@shamrock-core/common/analytics/firebase-analytics';
import React, { useState, Dispatch, SetStateAction, FC } from 'react';
import { Button, Chip, CircularProgress } from 'drift-ui';
import Toggle from '../../core/toggle';
import styles from '../styles/ptPlanDialog.scss';
import PtSubscriptionPlanSection from './PtSubscriptionPlanSection';
import {
  useGetPtSubscriptionPricingQuery,
  useCheckSubscriptionHistoryQuery,
  PTSubscriptionPricing,
} from '../../../state/paymentService';
import YearlyDiscount from './yearlyDiscount';
import { useTranslation } from 'react-i18next';
import ContactForm from './ContactForm';
import paymentCardCircle from '../../../assets/images/payment-card-circle.svg';
import { useSelector } from 'react-redux';
import { RootState } from '../../../bootstrap';
import Permission from '../../core/permission';

interface PtPlanDialogProps {
  handleNext: () => void;
  isMonthly?: boolean;
  setIsMonthly?: Dispatch<SetStateAction<boolean>>;
}

function PremiumStatus({ amount, period }: { amount: number; period: string }) {
  return (
    <div>
      <span className={styles.priceAmount}>{`$${amount?.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`}</span>
      <span className={styles.pricePeriod}>/{period}</span>
    </div>
  );
}

function getPTPricing(isMonthly: boolean, ptSubscriptionPricing: PTSubscriptionPricing, t: (key: string) => string) {
  if (isMonthly) {
    return {
      pricePeriod: t('month'),
      premiumPrice: ptSubscriptionPricing?.premium?.monthly,
      legacyPrice: ptSubscriptionPricing?.legacy?.monthly,
    };
  }

  return {
    pricePeriod: t('year'),
    premiumPrice: ptSubscriptionPricing?.premium?.yearly,
    legacyPrice: ptSubscriptionPricing?.legacy?.yearly,
  };
}

const PtPlanDialog: FC<PtPlanDialogProps> = ({ handleNext, isMonthly, setIsMonthly }) => {
  const [showContactForm, setShowContactForm] = useState(false);
  const { data: ptSubscriptionPricing, isLoading } = useGetPtSubscriptionPricingQuery();
  const { t } = useTranslation('common', { keyPrefix: 'proTransportSubscriptions' });
  const { data: hadPreviousSubscription } = useCheckSubscriptionHistoryQuery();
  const { pricePeriod, premiumPrice } = getPTPricing(isMonthly, ptSubscriptionPricing, t);
  const [subscriptionName, setSubscriptionName] = useState<'Enterprise' | 'Legacy' | undefined>(undefined);
  const permissions = useSelector((state: RootState) => state.app.permissions);

  const handleUpgradeToPremium = () => {
    FirebaseAnalytics.sendEvent(
      FirebaseAnalytics.EVENTS.CLICK,
      FirebaseAnalytics.MODULES.RTSPRO,
      FirebaseAnalytics.PAGES.SELECT_PLAN,
      {
        Description: 'Upgrade to Premium Click',
        PricePeriod: isMonthly ? 'Monthly' : 'Yearly',
      },
    );
    handleNext();
  };

  const handleContactYou = (subscriptionName: 'Enterprise' | 'Legacy') => {
    FirebaseAnalytics.sendEvent(
      FirebaseAnalytics.EVENTS.CLICK,
      FirebaseAnalytics.MODULES.RTSPRO,
      FirebaseAnalytics.PAGES.SELECT_PLAN,
      {
        Description: 'Contact You Click',
      },
    );
    setSubscriptionName(subscriptionName);
    setShowContactForm(true);
  };

  return (
    <>
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress size={80} />
        </div>
      )}
      {!isLoading && ptSubscriptionPricing && (
        <div className={styles.ptPlanDialog}>
          <div className={styles.titleContainer}>
            <div className={styles.selectPlan}>{t('select_plan')}</div>
            <Toggle
              leftText={t('Monthly')}
              rightText={t('Yearly')}
              onChange={(index) => setIsMonthly(index === 0)}
              selectedIndex={isMonthly ? 0 : 1}
            />
            <YearlyDiscount amount={ptSubscriptionPricing?.yearlyDiscountPercent} />
          </div>
          <div className={styles.subscriptionBlurbContainer}>
            <PtSubscriptionPlanSection
              title={t('protransport_basic')}
              status={t('included_free')}
              statusSubText={t('with_RTS_PRO')}
              includesTitle={t('included_with_basic')}
              includesText={[
                t('mobile_and_web_access'),
                t('load_management'),
                t('dispatcher_scheduler'),
                t('unlimited_users'),
                t('unlimited_storage'),
                t('IFTA'),
                t('direct_invoicing'),
                t('loads_on_credit'),
                t('manage_booked_loads'),
              ]}
              button={
                <Permission permission={'tms.user.basic'}>
                  <Chip color="primary" variant="outlined" label={t('current_plan')} />
                </Permission>
              }
            />
            <PtSubscriptionPlanSection
              title={t('protransport_premium')}
              status={<PremiumStatus amount={premiumPrice} period={pricePeriod} />}
              showFreeTrialChip={hadPreviousSubscription === false}
              includesTitle={t('included_with_premium')}
              includesSubtitle={t('everything_in_basic_plus')}
              includesText={[
                t('aging_reports'),
                t('driver_pay_statements'),
                t('wex_fuel_import'),
                t('dedicated_implementation'),
              ]}
              button={
                permissions.includes('tms.user.premium') ? (
                  <Chip color="primary" variant="outlined" label={t('current_plan')} />
                ) : (
                  <Button
                    className={styles.subscriptionButtons}
                    color="primary"
                    variant="contained"
                    onClick={handleUpgradeToPremium}
                  >
                    {t('upgrade_to_premium')}
                  </Button>
                )
              }
            />
            <PtSubscriptionPlanSection
              title={t('protransport_legacy')}
              status={t('custom_pricing')}
              includesTitle={t('included_with_legacy')}
              includesSubtitle={t('everything_in_premium_plus')}
              includesText={[
                t('exportable_reports'),
                t('quickbooks_integrations'),
                t('safety_maintenance'),
                t('eld_integrations'),
                t('edi_capabilities'),
                t('gps_integrations'),
              ]}
              button={
                <Button
                  className={styles.subscriptionButtons}
                  color="primary"
                  variant="contained"
                  onClick={() => handleContactYou('Legacy')}
                >
                  {t('get_a_quote')}
                </Button>
              }
            />
            <PtSubscriptionPlanSection
              title={t('protransport_enterprise')}
              status={t('coming_this_fall')}
              includesTitle={t('included_with_enterprise')}
              includesSubtitle={t('everything_in_premium_plus')}
              includesText={[
                t('exportable_reports'),
                t('quickbooks_integrations'),
                t('safety_maintenance'),
                t('load_status_alerts'),
              ]}
              button={
                <Button
                  className={styles.subscriptionButtons}
                  color="primary"
                  variant="contained"
                  onClick={() => handleContactYou('Enterprise')}
                >
                  {t('get_a_quote')}
                </Button>
              }
            />
          </div>
          <div className={styles.creditCardText}>
            <img src={paymentCardCircle} alt="Payment Card Icon" />
            <span>{t('pay_using_card')}</span>
          </div>
          {showContactForm && <ContactForm subscriptionName={subscriptionName} />}
        </div>
      )}
    </>
  );
};

export default PtPlanDialog;
